import { useEffect, useState, useRef } from "react";
import { getContentSourceText } from "@/lib/content/getContentSourceText";
import type { ContentItem } from "@/types/content";

const MAX_TOTAL_CHARS = 50000;

interface RoomContentTextResult {
  contentText: string | null;
  isLoading: boolean;
  groundedCount: number;
}

/**
 * Aggregates document text and media transcripts for all content items in a room.
 * Used to build a content-grounded system prompt for the Room AI Tutor.
 */
export function useRoomContentText(contents: ContentItem[]): RoomContentTextResult {
  const [contentText, setContentText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [groundedCount, setGroundedCount] = useState(0);

  // Cache key based on sorted content IDs
  const cacheKeyRef = useRef<string>("");
  const cachedTextRef = useRef<string | null>(null);
  const cachedCountRef = useRef<number>(0);

  useEffect(() => {
    if (!contents.length) {
      setContentText(null);
      setGroundedCount(0);
      setIsLoading(false);
      return;
    }

    const newCacheKey = contents.map((c) => c.id).sort().join(",");
    if (newCacheKey === cacheKeyRef.current && cachedTextRef.current !== null) {
      setContentText(cachedTextRef.current);
      setGroundedCount(cachedCountRef.current);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      setIsLoading(true);

      try {
        // Fetch all content texts in parallel
        const results = await Promise.all(
          contents.map(async (item) => {
            const text = await getContentSourceText(item.id, item.type);
            return { title: item.title, text };
          })
        );

        if (cancelled) return;

        // Filter items that have text and build aggregated string
        const withText = results.filter((r) => r.text);
        if (!withText.length) {
          cacheKeyRef.current = newCacheKey;
          cachedTextRef.current = null;
          cachedCountRef.current = 0;
          setContentText(null);
          setGroundedCount(0);
          return;
        }

        let aggregated = "";
        let count = 0;

        for (const item of withText) {
          const section = `### ${item.title}\n<content>\n${item.text}\n</content>\n\n`;
          if (aggregated.length + section.length > MAX_TOTAL_CHARS) {
            // Add truncated version if there's room
            const remaining = MAX_TOTAL_CHARS - aggregated.length;
            if (remaining > 200) {
              aggregated += `### ${item.title}\n<content>\n${item.text!.slice(0, remaining - 100)}\n[truncated]\n</content>\n\n`;
              count++;
            }
            break;
          }
          aggregated += section;
          count++;
        }

        cacheKeyRef.current = newCacheKey;
        cachedTextRef.current = aggregated;
        cachedCountRef.current = count;
        setContentText(aggregated);
        setGroundedCount(count);
      } catch (error) {
        console.error("Error aggregating room content text:", error);
        if (!cancelled) {
          setContentText(null);
          setGroundedCount(0);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [contents]);

  return { contentText, isLoading, groundedCount };
}
